import { FormEvent, useState } from 'react';
import { Navigate, useLocation, useNavigate, type Location } from 'react-router-dom';
import { Waypoints } from 'lucide-react';
import piigLogo from '../../assets/piig-logo.png';
import { getApiErrorMessage } from '../../api/client';
import { useAuthStore } from '../../store/auth.store';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Field, Input } from '../../components/ui/Form';
import { PasswordInput } from '../../components/ui/PasswordInput';

const DEFAULT_BRAND_NAME = 'PILOT INNOVATION GROUP CO.,LTD.';

/** Where to go after sign-in: the guarded URL the user came from, query string included. */
function redirectTarget(state: unknown): string {
  const from = (state as { from?: Partial<Location> } | null)?.from;
  if (!from?.pathname || from.pathname === '/login') return '/projects';
  return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
}

/** POST /auth/login; the session lives in memory only, the refresh token in the httpOnly pert_refresh_token cookie. */
export function LoginPage() {
  const status = useAuthStore((s) => s.status);
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const brandName = (import.meta.env.VITE_BRAND_NAME as string | undefined) || DEFAULT_BRAND_NAME;
  const logoUrl = (import.meta.env.VITE_BRAND_LOGO_URL as string | undefined) || piigLogo;

  // Already signed in (e.g. silent refresh succeeded) — don't show the login form.
  if (status === 'authenticated') {
    return <Navigate to={redirectTarget(location.state)} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email: email.trim(), password });
      navigate(redirectTarget(location.state), { replace: true });
    } catch (err) {
      setError(getApiErrorMessage(err, 'อีเมลหรือรหัสผ่านไม่ถูกต้อง'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="w-full max-w-[400px] rounded-2xl border border-border bg-surface p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-2xl text-white"
            style={{ background: 'linear-gradient(155deg, #1D5AA6, #154580)' }}
          >
            <Waypoints size={26} />
          </div>
          <div>
            <h1 className="font-display text-xl font-bold text-text-primary">Multi-PERT</h1>
            <p className="text-xs text-text-secondary">ระบบวางแผนและติดตามหลายโปรเจกต์ด้วย PERT</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          <Field label="อีเมล">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              disabled={submitting}
              placeholder="name@company.com"
            />
          </Field>
          <Field label="รหัสผ่าน">
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={submitting}
            />
          </Field>

          {error && <Alert>{error}</Alert>}

          <Button type="submit" className="h-11 w-full" loading={submitting} disabled={!email || !password}>
            เข้าสู่ระบบ
          </Button>
        </form>

        <div className="mt-6 flex items-center justify-center gap-2">
          <img src={logoUrl} alt={brandName} className="h-5 w-5 object-contain" />
          <p className="text-center text-xs text-text-secondary">{brandName}</p>
        </div>
      </div>
    </div>
  );
}
